import { DomainError } from '@telespot/domain';

/**
 * How much retry budget one replay of a given failure costs.
 *
 * The third question retrying asks, after the {@link retryable} marks (may I replay
 * this?) and the {@link RetryDecider} (is this transient?): what does replaying it cost
 * the downstream? A failure can be perfectly transient and still be expensive to retry.
 */
export interface RetryCost<T extends Error = Error> {
  /** Must be finite and greater than 0; a zero cost would silently disable the budget. */
  of(error: T): number;
}

/**
 * Default pricing, weighted by what the failure says about the downstream's state.
 *
 * Timeouts are the most expensive: the request is probably still running on the other
 * side, so the replay doubles work on a service already too slow to answer.
 *
 * Classifies on the error's `code`, then its `name`, then its status — never its
 * message, which is prose.
 */
export class WeightedRetryCost implements RetryCost {
  /** Failed fast: retrying costs the downstream about what the original call did. */
  public static readonly DEFAULT = 1;

  /** The downstream says it cannot serve right now (502, 503). */
  public static readonly DISTRESSED = 2;

  /** The downstream asked for less traffic (429, `SlowDown`, `ThrottlingException`). */
  public static readonly THROTTLED = 3;

  /** No answer within the deadline (408, 504, `ETIMEDOUT`, a `TimeoutError`). */
  public static readonly TIMED_OUT = 5;

  private static readonly TIMEOUT = /time(d)?[_\s-]?out|deadline/i;

  private static readonly THROTTLE = /throttl|too[_\s-]?many|rate[_\s-]?limit|slow[_\s-]?down|limit[_\s-]?exceeded/i;

  private static readonly COST_BY_STATUS = new Map<number, number>([
    [408, WeightedRetryCost.TIMED_OUT],
    [504, WeightedRetryCost.TIMED_OUT],
    [429, WeightedRetryCost.THROTTLED],
    [502, WeightedRetryCost.DISTRESSED],
    [503, WeightedRetryCost.DISTRESSED],
  ]);

  public of(error: Error): number {
    const signals = [WeightedRetryCost.codeOf(error), error.name];

    if (signals.some((signal) => WeightedRetryCost.TIMEOUT.test(signal))) return WeightedRetryCost.TIMED_OUT;
    if (signals.some((signal) => WeightedRetryCost.THROTTLE.test(signal))) return WeightedRetryCost.THROTTLED;

    return WeightedRetryCost.COST_BY_STATUS.get(WeightedRetryCost.statusOf(error)) ?? WeightedRetryCost.DEFAULT;
  }

  /** `DomainError.code`, an AWS SDK code, or a Node `ETIMEDOUT`-style one. */
  private static codeOf(error: Error): string {
    const code = (error as { code?: unknown }).code;

    return typeof code === 'string' ? code : '';
  }

  /** `0` when the error carries no status, which no entry in the table matches. */
  private static statusOf(error: Error): number {
    if (error instanceof DomainError) return error.httpStatus;

    const status = (error as { statusCode?: unknown }).statusCode;

    return typeof status === 'number' ? status : 0;
  }
}
